import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLocale } from '@/contexts/LocaleContext'
import { useTeam } from '@/hooks/useTeam'
import {
  getTeamErrorCode,
  teamService,
  type Team,
  type TeamInvitation,
  type TeamMember,
  type TeamUsage,
} from '@/services/teamService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import SectionHeader from '@/components/researcher/SectionHeader'
import UsagePanel from '@/components/researcher/team/UsagePanel'

/** A members-table row: either a real member or a seat held by an invitation. */
type Row =
  | { kind: 'member'; member: TeamMember }
  | { kind: 'invitation'; invitation: TeamInvitation }

export default function TeamPage() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('team')
  const { language } = useLocale()
  const navigate = useNavigate()
  const { isOwner } = useTeam()

  // undefined = loading, null = no team / failed
  const [team, setTeam] = useState<Team | null | undefined>(undefined)
  // Loaded alongside the team; null when the request failed, so a usage
  // hiccup never blanks the members table.
  const [usage, setUsage] = useState<TeamUsage | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteError, setInviteError] = useState('')
  const [inviting, setInviting] = useState(false)
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: Row } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    teamService
      .getTeam()
      .then((data) => !cancelled && setTeam(data))
      .catch(() => !cancelled && setTeam(null))
    teamService
      .getUsage()
      .then((data) => !cancelled && setUsage(data))
      .catch(() => !cancelled && setUsage(null))
    return () => {
      cancelled = true
    }
  }, [])

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }),
    [language],
  )

  /** Minutes per person in the current period, keyed by user id. */
  const minutesByUser = useMemo(() => {
    const map = new Map<number, number>()
    for (const row of usage?.per_member ?? []) {
      if (row.user_id != null) map.set(row.user_id, row.minutes)
    }
    return map
  }, [usage])

  const rows = useMemo<Row[]>(() => {
    if (!team) return []
    return [
      ...team.members.map((member) => ({ kind: 'member' as const, member })),
      ...team.invitations.map((invitation) => ({ kind: 'invitation' as const, invitation })),
    ]
  }, [team])

  const handleInvite = useCallback(async () => {
    setInviting(true)
    setInviteError('')
    try {
      const updated = await teamService.invite(inviteEmail.trim())
      setTeam(updated)
      setInviteOpen(false)
      setInviteEmail('')
    } catch (error) {
      const code = getTeamErrorCode(error)
      setInviteError(code ? t(`invite.errors.${code}`, { defaultValue: t('invite.errors.generic') }) : t('invite.errors.generic'))
    } finally {
      setInviting(false)
    }
  }, [inviteEmail, t])

  const closeMenu = () => setMenu(null)

  const handleRemoveMember = useCallback(async (userId: number) => {
    setBusy(true)
    try {
      setTeam(await teamService.removeMember(userId))
    } finally {
      setBusy(false)
      closeMenu()
    }
  }, [])

  const handleRevoke = useCallback(async (invitationId: number) => {
    setBusy(true)
    try {
      setTeam(await teamService.revokeInvitation(invitationId))
    } finally {
      setBusy(false)
      closeMenu()
    }
  }, [])

  if (team === undefined) {
    return (
      <ResearcherShell active="team" crumbs={[t('title')]} hideTopBarAction>
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      </ResearcherShell>
    )
  }

  if (team === null) {
    return (
      <ResearcherShell active="team" crumbs={[t('title')]} hideTopBarAction>
        <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'center', py: 8 }}>
          <GroupsOutlinedIcon sx={{ fontSize: 44, color: c.ink32, mb: 1.5 }} />
          <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontSize: 20, fontWeight: 700, mb: 1 }}>
            {t('noTeam.title')}
          </Typography>
          <Typography sx={{ color: c.ink56, mb: 3 }}>{t('noTeam.body')}</Typography>
          <Button variant="outlined" onClick={() => navigate('/dashboard')}>
            {t('noTeam.back')}
          </Button>
        </Box>
      </ResearcherShell>
    )
  }

  const seatsFull = team.seats.used >= team.seats.limit
  const licenceLabel = team.licence.expires_at
    ? t('header.licenceUntil', { date: dateFmt.format(new Date(team.licence.expires_at)) })
    : t('header.licenceOpen')

  return (
    <ResearcherShell active="team" crumbs={[t('title')]} hideTopBarAction>
      <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Header: who we are, what the licence covers */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between' }}
        >
          <Box>
            <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontSize: 24, fontWeight: 700 }}>
              {team.name}
            </Typography>
            {team.organization && (
              <Typography sx={{ color: c.ink56, fontSize: 14, mt: 0.25 }}>{team.organization}</Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 1 }}>
              <Chip
                size="small"
                label={licenceLabel}
                sx={{
                  bgcolor: team.licence.is_active ? alpha(c.coral, 0.08) : c.gray100,
                  color: team.licence.is_active ? c.coralHover : c.ink56,
                  fontWeight: 600,
                }}
              />
              <Chip
                size="small"
                label={t('header.seats', { used: team.seats.used, limit: team.seats.limit })}
                sx={{ bgcolor: c.gray100, color: c.ink, fontWeight: 600 }}
              />
            </Stack>
          </Box>

          {isOwner && (
            <Button
              variant="contained"
              startIcon={<PersonAddAltOutlinedIcon />}
              disabled={seatsFull}
              onClick={() => setInviteOpen(true)}
            >
              {t('header.invite')}
            </Button>
          )}
        </Stack>

        {seatsFull && isOwner && (
          <Box
            sx={{
              p: 1.75,
              borderRadius: '12px',
              bgcolor: alpha(c.coral, 0.05),
              border: `1px solid ${alpha(c.coral, 0.25)}`,
              fontSize: 14,
              color: c.ink,
            }}
          >
            {t('header.seatsFull', { limit: team.seats.limit })}
          </Box>
        )}

        {/* Members */}
        <Box sx={{ bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '16px', overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, pt: 2.25, pb: 1 }}>
            <SectionHeader title={t('members.title')} />
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', minWidth: 620, borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  {['person', 'role', 'status', 'studies', 'minutes'].map((key) => (
                    <Box
                      key={key}
                      component="th"
                      sx={{
                        textAlign: key === 'studies' || key === 'minutes' ? 'right' : 'left',
                        px: 2.5,
                        py: 1.25,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: c.ink48,
                        bgcolor: c.gray50,
                        borderBottom: `1px solid ${c.ink08}`,
                      }}
                    >
                      {t(`members.columns.${key}`)}
                    </Box>
                  ))}
                  <Box component="th" sx={{ width: 52, bgcolor: c.gray50, borderBottom: `1px solid ${c.ink08}` }} />
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map((row) => {
                  const isInvitation = row.kind === 'invitation'
                  const email = isInvitation ? row.invitation.email : row.member.email
                  const name = isInvitation ? '' : row.member.full_name
                  const rowKey = isInvitation ? `i${row.invitation.id}` : `m${row.member.id}`
                  const canAct = isOwner && (isInvitation || row.member.role !== 'owner')
                  return (
                    <Box component="tr" key={rowKey} sx={{ '&:hover': { bgcolor: c.gray50 } }}>
                      <Box component="td" sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${c.ink08}` }}>
                        {name && (
                          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{name}</Typography>
                        )}
                        <Typography sx={{ fontSize: 12.5, color: c.ink48 }}>{email}</Typography>
                      </Box>
                      <Box component="td" sx={{ px: 2.5, py: 1.5, fontSize: 14, borderBottom: `1px solid ${c.ink08}` }}>
                        {isInvitation ? t('members.roles.member') : t(`members.roles.${row.member.role}`)}
                      </Box>
                      <Box component="td" sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${c.ink08}` }}>
                        <Chip
                          size="small"
                          label={isInvitation ? t('members.status.invited') : t('members.status.active')}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: isInvitation ? alpha(theme.affexy.emotion.surprise, 0.14) : alpha(theme.affexy.emotion.attention, 0.14),
                            color: isInvitation ? '#8A5A12' : '#2C6F66',
                          }}
                        />
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          fontSize: 14,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color: isInvitation ? c.ink32 : c.ink,
                          borderBottom: `1px solid ${c.ink08}`,
                        }}
                      >
                        {isInvitation ? '—' : row.member.studies_count}
                      </Box>
                      <Box
                        component="td"
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          fontSize: 14,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color: isInvitation ? c.ink32 : c.ink,
                          borderBottom: `1px solid ${c.ink08}`,
                        }}
                      >
                        {isInvitation ? '—' : minutesByUser.get(row.member.user_id) ?? 0}
                      </Box>
                      <Box component="td" sx={{ px: 1, py: 1.5, borderBottom: `1px solid ${c.ink08}` }}>
                        {canAct && (
                          <IconButton
                            size="small"
                            aria-label={t('members.rowMenu')}
                            onClick={(e) => setMenu({ anchor: e.currentTarget, row })}
                          >
                            <MoreHorizOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          </Box>

          {rows.length <= 1 && (
            <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
              <Typography sx={{ color: c.ink56, fontSize: 14 }}>
                {t('members.empty', { free: team.seats.limit - team.seats.used })}
              </Typography>
            </Box>
          )}
        </Box>

        {usage && <UsagePanel usage={usage} />}
      </Box>

      {/* Row menu */}
      <Menu anchorEl={menu?.anchor ?? null} open={Boolean(menu)} onClose={closeMenu}>
        {menu?.row.kind === 'member' && (
          <MenuItem
            disabled={busy}
            onClick={() => handleRemoveMember((menu.row as { member: TeamMember }).member.user_id)}
          >
            {t('members.actions.remove')}
          </MenuItem>
        )}
        {menu?.row.kind === 'invitation' && (
          <MenuItem
            disabled={busy}
            onClick={() => handleRevoke((menu.row as { invitation: TeamInvitation }).invitation.id)}
          >
            {t('members.actions.revoke')}
          </MenuItem>
        )}
      </Menu>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => !inviting && setInviteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700 }}>
          {t('invite.title')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: c.ink56, fontSize: 14, mb: 2 }}>
            {t('invite.body', { free: team.seats.limit - team.seats.used })}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            size="small"
            label={t('invite.emailLabel')}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            error={Boolean(inviteError)}
            helperText={inviteError || ' '}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && inviteEmail.trim() && !inviting) handleInvite()
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setInviteOpen(false)} disabled={inviting}>
            {t('invite.cancel')}
          </Button>
          <Button variant="contained" onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
            {inviting ? t('invite.sending') : t('invite.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </ResearcherShell>
  )
}
